import React, { useState, useEffect } from 'react';
import { Award, TrendingUp, ChartBar, Star } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Milestone, MilestoneType } from '../../hooks/milestones/types';

interface MilestoneNotificationProps {
  milestone: Milestone | null;
}

const MilestoneNotification: React.FC<MilestoneNotificationProps> = ({ milestone }) => {
  const [visible, setVisible] = useState(false);
  
  useEffect(() => {
    if (milestone) {
      setVisible(true);
      const timer = setTimeout(() => {
        setVisible(false);
      }, 6000);
      
      return () => clearTimeout(timer);
    }
  }, [milestone]);
  
  if (!milestone) return null;
  
  const getIcon = () => {
    switch (milestone.type) {
      case 'achievement':
        return (
          <motion.div
            initial={{ rotate: -10, scale: 0.8 }}
            animate={{ rotate: 0, scale: 1 }}
            transition={{
              type: "spring",
              stiffness: 260,
              damping: 20,
              delay: 0.2
            }}
          >
            <Star className="h-8 w-8 text-yellow-300" />
          </motion.div>
        );
      case 'skill':
        return (
          <motion.div
            initial={{ rotate: -10, scale: 0.8 }}
            animate={{ rotate: 0, scale: 1 }}
            transition={{
              type: "spring",
              stiffness: 260,
              damping: 20,
              delay: 0.2
            }}
          >
            <ChartBar className="h-8 w-8 text-web3-green" />
          </motion.div>
        );
      case 'kpi':
        return (
          <motion.div
            initial={{ rotate: -10, scale: 0.8 }}
            animate={{ rotate: 0, scale: 1 }}
            transition={{
              type: "spring",
              stiffness: 260,
              damping: 20,
              delay: 0.2
            }}
          >
            <TrendingUp className="h-8 w-8 text-bitcoin-accent" />
          </motion.div>
        );
      case 'career':
        return (
          <motion.div
            initial={{ rotate: -10, scale: 0.8 }}
            animate={{ rotate: 0, scale: 1 }}
            transition={{
              type: "spring",
              stiffness: 260,
              damping: 20,
              delay: 0.2
            }}
          >
            <Award className="h-8 w-8 text-web3-green" />
          </motion.div>
        );
      default:
        return (
          <motion.div
            initial={{ rotate: -10, scale: 0.8 }}
            animate={{ rotate: 0, scale: 1 }}
            transition={{
              type: "spring",
              stiffness: 260,
              damping: 20,
              delay: 0.2
            }}
          >
            <Award className="h-8 w-8 text-web3-green" />
          </motion.div>
        );
    }
  };
  
  const getBgColor = () => {
    switch (milestone.type) {
      case 'achievement':
        return 'from-yellow-500/20 to-amber-600/10 border-yellow-500/30';
      case 'skill':
        return 'from-web3-green/20 to-green-600/10 border-web3-green/30';
      case 'kpi':
        return 'from-bitcoin-accent/20 to-bitcoin-dark/10 border-bitcoin-accent/30';
      case 'career':
        return 'from-web3-blue/20 to-blue-600/10 border-web3-blue/30';
      default:
        return 'from-web3-green/20 to-green-600/10 border-web3-green/30';
    }
  };
  
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.9 }}
          className="fixed bottom-6 right-6 z-50 max-w-md"
          transition={{ duration: 0.4, type: "spring", stiffness: 120 }}
        >
          <motion.div 
            className={`rounded-lg shadow-lg border p-4 bg-gradient-to-br ${getBgColor()} backdrop-blur-md`}
            initial={{ x: 20 }}
            animate={{ x: 0 }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 20,
              delay: 0.1
            }}
          >
            <div className="flex items-center">
              <div className="flex-shrink-0 mr-4">
                {getIcon()}
              </div>
              <div>
                <motion.h3 
                  className="font-bold text-white text-lg"
                  initial={{ y: 5, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  {milestone.title}
                </motion.h3>
                <motion.p 
                  className="text-white/80"
                  initial={{ y: 5, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  {milestone.description}
                </motion.p>
              </div>
            </div>
            
            <motion.div 
              className="mt-2 w-full h-1 bg-white/10 rounded-full overflow-hidden"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.3, delay: 0.4 }}
            >
              <motion.div 
                className="h-full bg-white/30"
                initial={{ width: "100%" }}
                animate={{ width: "0%" }}
                transition={{ duration: 6, ease: "linear" }}
              />
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MilestoneNotification;
